import { useEffect, useState } from "react";
import { formatMoneyIntl } from "../../lib/formatMoney"; // relative: must REALLY resolve under Jest (no "@/" alias there)
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { ASSETS } from "@/lib/content";
import { api } from "@/lib/api";
import { buildFooterModel, buildNavModel, resolveBrandName } from "@/lib/siteLinks";

/**
 * useSiteContent — normalized view of the public site's content, shared by ALL templates,
 * so that a template only ever presents (it varies the visuals, never the data).
 *
 * The i18n + content_blocks + tenant-record layer is NOT resolved here: `useSiteCopy()`
 * (lib/useSiteCopy.js, over lib/siteContent.js `resolveSiteCopy`) is the single resolver
 * shared with the Classic components — including the contact e-mail's
 * `override > business_email > owner_email > sample` chain, which this hook used to
 * re-derive on its own while Classic's Contact section resolved it without the tenant at
 * all. This hook only adds what is specific to the templates: the brand name, the hero
 * stats shape and the footer link model.
 *
 * @param {object} [toggles] `tenant.section_toggles` — needed for `navModel`/`footerModel`,
 *   whose section anchors and journal link follow the same visibility rules as the page's
 *   sections. Pass the same object the template gates its sections on.
 */
export function useSiteContent(toggles = {}) {
  const services = useServices();
  const testimonials = useTestimonials();
  const posts = useBlogPosts();
  // A menu may never offer a visitor a section that is not on the page. These three gates are
  // therefore the SAME expression each template gates the section itself on (e.g.
  // MinimalTemplate.jsx: `show("services") && services.length > 0`), reading the SAME hook
  // return — so nav, footer and section cannot disagree by construction.
  //
  // Derive these from anything else — the raw fetched array, a separate loading flag, the
  // tenant record — and the two halves start answering different questions: a section built
  // from the fallback sample copy would render while a nav built from the fetched rows hid its
  // link, which is the original defect inverted rather than fixed. The completion and failure
  // semantics that make this correct live in the three hooks below, which return `[]` until a
  // read has settled and on any failure, so this expression inherits them.
  const effectiveToggles = { ...toggles, services: toggles.services !== false && services.length > 0, testimonials: toggles.testimonials !== false && testimonials.length > 0, blog: toggles.blog !== false && posts.length > 0 };
  const { override, lang, setLang } = useLanguage();
  const siteCopy = useSiteCopy();
  const tt = useT("templates");
  const { tenant } = useTenant();

  const brandName = resolveBrandName(tenant, tt("shared.brand.fallback"));
  const branding = tenant?.branding || {};
  const hero = siteCopy.hero;

  return {
    ...siteCopy,
    // Sitemap, derived once for the six themes and for BOTH chromes (lib/siteLinks.js): the
    // header's destinations and the footer's columns can no longer disagree, which is what
    // BUG-006 was — five themed headers that led nowhere but back into the same page.
    navModel: buildNavModel({ siteCopy, tenant, toggles: effectiveToggles, brand: brandName }),
    footerModel: buildFooterModel({ siteCopy, tenant, toggles: effectiveToggles, brand: brandName }),
    lang,
    setLang,
    tenant,
    branding,
    brandName,
    hero: {
      ...hero,
      stats: [
        { v: hero.stat1, l: hero.stat1Label },
        { v: hero.stat2, l: hero.stat2Label },
        { v: hero.stat3, l: hero.stat3Label },
      ],
    },
    footer: { ...siteCopy.footer, brand: brandName },
    images: {
      ...siteCopy.images,
      // The tenant's uploaded logo is the fallback BEFORE the bundled placeholder.
      logo: override("site.logo") || branding.logo_url || ASSETS.logo,
    },
  };
}

/**
 * The practice's real testimonials, falling back to the labelled sample copy only when the
 * fetch genuinely succeeded and returned none — never when it failed. A rejected request, a
 * 403 (what a suspended/pending tenant returns on every public read), and a malformed
 * non-array 200 body are load failures, not "this practice has none": they set `failed`,
 * which forces an empty return (the section-level `.length > 0` gate then hides the section
 * entirely, so nothing fabricated ever renders). `failed` is cleared on every fetch attempt
 * that comes back well-formed — success or genuine-empty alike — so a later retry (the effect
 * re-runs on `[lang]`, e.g. a visitor switching the site's language) un-hides real content
 * instead of latching the failure forever.
 */
export function useTestimonials() {
  const { t, lang } = useLanguage();
  const [remote, setRemote] = useState(null);
  const [failed, setFailed] = useState(false);
  // Until the first read SETTLES this returns nothing, matching the contract Classic's
  // components/site/Testimonials.jsx already implements for the same endpoint: painting the
  // samples first showed every visitor "Sample testimonial" for the width of a round trip,
  // including on a practice with real ones. `failed` alone could not express that — before any
  // response arrives a request has neither succeeded nor failed, and treating "not yet" as
  // "succeeded and empty" is what surfaced the bundled copy. Set once and never reset, so a
  // later re-run (the effect re-fires on `[lang]`) does not blank a section that is already
  // showing content.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    // Guards every write below against a stale run: `lang` changing re-fires this effect
    // before the previous fetch has settled, and its eventual resolution — success or
    // failure — must not land on the setters of a run nobody cares about anymore.
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/testimonials");
        if (ignore) return;
        if (!Array.isArray(data)) {
          setFailed(true);
          return;
        }
        setFailed(false);
        setRemote(
          data.length > 0
            ? data.map((r) => ({
                name: r.name,
                quote: lang === "en" ? r.quote_en || r.quote_fr : r.quote_fr || r.quote_en,
                url: r.url || "",
              }))
            : null
        );
      } catch {
        if (!ignore) setFailed(true);
      } finally {
        if (!ignore) setSettled(true);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [lang]);
  return !settled || failed ? [] : remote || t.testimonials.items;
}

// A real service's price for the "tag" slot of the card shape (see useServices below),
// through the one money module: locale by UI language, the SERVICE's currency, never a
// default — `/public/booking/services` always states one.
const formatServicePrice = (cents, currency, lang) => formatMoneyIntl(cents, currency, lang);

/**
 * Real active services from the tenant's booking catalogue (GET /public/booking/services),
 * falling back to the static sample catalogue (lib/content.js) only when the tenant has
 * none active yet (BUG-040 — this endpoint existed with zero frontend callers). Same card
 * shape either way: {tag, title, duration, body}, mapped from
 * {price_cents+currency, name, duration_min, description}.
 *
 * A `BookingService` carries no _fr/_en split (unlike testimonials and blog posts), so
 * `name`/`description` render as-is in every UI language — a deliberate product decision
 * recorded here, not a missing translation.
 *
 * As with `useTestimonials()`, a failed load (rejection, 403, malformed body) must not
 * present the bundled sample services as the tenant's own, so it is tracked separately via
 * `failed` — set on any failed attempt, cleared on any well-formed one, so a retry recovers.
 */
export function useServices() {
  const { t, lang } = useLanguage();
  const [remote, setRemote] = useState(null);
  const [failed, setFailed] = useState(false);
  // See `useTestimonials` above: an in-flight request is neither a success nor a failure, and
  // returning the sample catalogue while it is still open advertised services the practice may
  // not offer. Set once, never reset.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    // Guards every write below against a stale run: `lang` changing re-fires this effect
    // before the previous fetch has settled, and its eventual resolution — success or
    // failure — must not land on the setters of a run nobody cares about anymore.
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/public/booking/services");
        if (ignore) return;
        if (!Array.isArray(data)) {
          setFailed(true);
          return;
        }
        setFailed(false);
        setRemote(
          data.length > 0
            ? data.map((s) => ({
                tag: formatServicePrice(s.price_cents, s.currency, lang),
                title: s.name,
                duration: `${s.duration_min} min`,
                body: s.description || "",
              }))
            : null
        );
      } catch {
        if (!ignore) setFailed(true);
      } finally {
        if (!ignore) setSettled(true);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [lang]);
  return !settled || failed ? [] : remote || t.services.items;
}

/**
 * The practice's real blog posts, falling back to the labelled sample journal only when the
 * fetch genuinely succeeded and returned none — never when it failed. Same failure/empty
 * split as `useTestimonials()` and `useServices()` above: `failed` is set on any failed
 * attempt (rejection, 403, malformed body) and cleared on any well-formed one, so a retry
 * (the effect re-runs on `[lang]`) recovers instead of latching.
 */
const BLOG_COVERS = [ASSETS.mortar, ASSETS.apothecary, ASSETS.handMassage];
export function useBlogPosts() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);
  const [failed, setFailed] = useState(false);
  // See `useTestimonials` above. This one also gates the `/journal` interior-page link
  // (lib/siteLinks.js INTERIOR_PAGES), so an in-flight read used to put a menu entry for the
  // archive in front of a visitor before anything was known about it. Set once, never reset.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    // Guards every write below against a stale run: `lang` changing re-fires this effect
    // before the previous fetch has settled, and its eventual resolution — success or
    // failure — must not land on the setters of a run nobody cares about anymore.
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/blog");
        if (ignore) return;
        if (!Array.isArray(data)) {
          setFailed(true);
          return;
        }
        setFailed(false);
        setPosts(
          data.length > 0
            ? data.slice(0, 3).map((p, i) => ({
                slug: p.slug,
                tag: lang === "en" ? p.tag_en || p.tag_fr : p.tag_fr || p.tag_en,
                title: lang === "en" ? p.title_en || p.title_fr : p.title_fr || p.title_en,
                excerpt: lang === "en" ? p.excerpt_en || p.excerpt_fr : p.excerpt_fr || p.excerpt_en,
                date: new Date(p.created_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", { month: "long", year: "numeric" }),
                cover: p.cover_url || BLOG_COVERS[i % BLOG_COVERS.length],
              }))
            : null
        );
      } catch {
        if (!ignore) setFailed(true);
      } finally {
        if (!ignore) setSettled(true);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [lang]);
  if (!settled || failed) return [];
  return posts || t.blog.items.map((it, i) => ({ ...it, cover: BLOG_COVERS[i % BLOG_COVERS.length] }));
}
